// components/models/VectorControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import type { ArrowReasoningEffort, ArrowVectorParams, IdeogramVectorParams, RecraftVectorParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { IDEOGRAM_VECTOR_QUALITIES, IDEOGRAM_VECTOR_QUALITY_CREDITS, isIdeogramVectorModelId, isRecraftVectorModelId, normalizeIdeogramVectorQuality, normalizeVectorHex } from '#lib/vector-models';

export const getDefaultRecraftVectorParams = (): RecraftVectorParams => ({
  prompt: '',
  seed: -1,
  colors: [],
  background_color: null,
});

export const getDefaultArrowVectorParams = (): ArrowVectorParams => ({
  prompt: '',
  seed: -1,
  instructions: '',
  colors: [],
  reasoning_effort: 'medium',
});

export const getDefaultIdeogramVectorParams = (): IdeogramVectorParams => ({
  prompt: '',
  seed: -1,
  colors: [],
  quality: 'medium',
});

type VectorParams = RecraftVectorParams | ArrowVectorParams | IdeogramVectorParams;

const defaultParamsFor = (model: string): VectorParams =>
  isRecraftVectorModelId(model) ? getDefaultRecraftVectorParams() : isIdeogramVectorModelId(model) ? getDefaultIdeogramVectorParams() : getDefaultArrowVectorParams();

interface VectorControlPanelProps {
  params: VectorParams;
  setModelParams: (
    updater: (prevParams: { [model: string]: VectorParams }) => { [model: string]: VectorParams }
  ) => void;
  diffusionModel: string;
}

const parseHexList = (value: string): string[] =>
  value.split(/[\s,;]+/).map(normalizeVectorHex).filter((hex): hex is string => hex !== null);

const inputClass = 'w-full h-8 rounded-md border border-[#2A2A2A] bg-[#141414] px-2 text-xs text-white/75 outline-none focus:border-white/20';

const EFFORT_OPTIONS: { label: string; value: ArrowReasoningEffort }[] = [
  { label: 'Fast', value: 'low' },
  { label: 'Balanced', value: 'medium' },
  { label: 'Careful', value: 'high' },
  { label: 'Most careful', value: 'xhigh' },
];
const EFFORT_LABELS = EFFORT_OPTIONS.map((o) => o.label);
const QUALITY_OPTIONS = IDEOGRAM_VECTOR_QUALITIES.map((value) => ({
  label: `${value[0]?.toUpperCase()}${value.slice(1)} · ${IDEOGRAM_VECTOR_QUALITY_CREDITS[value]} credits`,
  value,
}));
const QUALITY_LABELS = QUALITY_OPTIONS.map((o) => o.label);
const FULL_WIDTH_STYLE: React.CSSProperties = { width: '100%' };

// Written to the store on blur; the key remounts the field when the store value changes under it (a model switch, an agent write).
const LabeledInput: React.FC<{ id: string; label: string; name: string; defaultValue: string; placeholder: string; onBlur: (e: React.FocusEvent<HTMLInputElement>) => void; help?: string }> = ({ id, label, name, defaultValue, placeholder, onBlur, help }) => (
  <div key={`${id}:${defaultValue}`} className="mb-4">
    <label className="block text-sm font-medium text-white/70 mb-1" htmlFor={id}>{label}</label>
    <input id={id} className={inputClass} name={name} defaultValue={defaultValue} onBlur={onBlur} placeholder={placeholder} />
    {help ? <p className="text-xs text-[#4D4D4D] mt-1">{help}</p> : null}
  </div>
);

const VectorControlPanel: React.FC<VectorControlPanelProps> = ({ params, setModelParams, diffusionModel }) => {
  const { kernel } = useKernel();
  const isRecraft = isRecraftVectorModelId(diffusionModel);
  const isIdeogram = isIdeogramVectorModelId(diffusionModel);

  const updateParam = useCallback((name: string, value: unknown) => {
    setModelParams((prevModelParams) => {
      const currentParams = prevModelParams[diffusionModel] ?? defaultParamsFor(diffusionModel);
      return { ...prevModelParams, [diffusionModel]: { ...currentParams, [name]: value } as VectorParams };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: typeof value === 'string' ? undefined : value,
      value_length: typeof value === 'string' ? value.length : undefined,
      model_name: diffusionModel,
      source_component: 'VectorControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    updateParam(e.target.name, e.target.value);
  }, [updateParam]);

  const handleColorsChange = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    updateParam('colors', parseHexList(e.target.value));
  }, [updateParam]);

  const handleOptionalHexChange = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    updateParam(e.target.name, normalizeVectorHex(e.target.value));
  }, [updateParam]);

  const handleEffortChange = useCallback((label: string) => {
    updateParam('reasoning_effort', EFFORT_OPTIONS.find((o) => o.label === label)?.value ?? 'medium');
  }, [updateParam]);

  const handleQualityChange = useCallback((label: string) => {
    updateParam('quality', QUALITY_OPTIONS.find((o) => o.label === label)?.value ?? 'medium');
  }, [updateParam]);

  const recraft = params as RecraftVectorParams;
  const arrow = params as ArrowVectorParams;
  const ideogram = params as IdeogramVectorParams;

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the logo, icon or illustration..."
          description="Subject, style and composition. The artboard sets the size; the colours below set the palette."
          name="prompt"
          value={params.prompt ?? ''}
          onChange={handleTextChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      {!isRecraft && !isIdeogram && (
        <div className="mb-4">
          <AutoGrowingTextarea
            label="Style"
            placeholder="flat, geometric, two colours, rounded corners"
            description="Optional guidance on the look, kept apart from what to draw."
            name="instructions"
            value={arrow.instructions ?? ''}
            onChange={handleTextChange}
            maxHeight={200}
            labelClassName="block text-sm font-medium text-white/70 mb-1"
          />
        </div>
      )}

      <LabeledInput id="vectorColors" label="Brand colours" name="colors" defaultValue={(params.colors ?? []).join(', ')} onBlur={handleColorsChange} placeholder="#1d4ed8, #f59e0b" help="Hex colours the artwork should use, comma separated. Leave empty to let the model choose." />

      {isIdeogram ? (
        <div className="mb-4">
          <Select
            id="ideogramVectorQuality"
            label="Quality"
            value={QUALITY_OPTIONS.find((o) => o.value === normalizeIdeogramVectorQuality(ideogram.quality))?.label ?? ''}
            options={QUALITY_LABELS}
            onChange={handleQualityChange}
            style={FULL_WIDTH_STYLE}
          />
        </div>
      ) : isRecraft ? (
        <LabeledInput id="vectorBackground" label="Background colour" name="background_color" defaultValue={recraft.background_color ?? ''} onBlur={handleOptionalHexChange} placeholder="#ffffff" help="Optional hex. Empty lets the model decide; a logo usually wants a plain one." />
      ) : (
        <div className="mb-4">
          <Select
            id="arrowEffort"
            label="Care"
            value={EFFORT_OPTIONS.find((o) => o.value === arrow.reasoning_effort)?.label ?? 'Balanced'}
            options={EFFORT_LABELS}
            onChange={handleEffortChange}
            style={FULL_WIDTH_STYLE}
          />
          <p className="text-xs text-[#4D4D4D] mt-1">More care takes longer and costs more; the price follows what the model used.</p>
        </div>
      )}
    </>
  );
};

export default memo(VectorControlPanel);
